import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { actions as notificationActions } from 'store/notificationsManager'
import TrainingReport from './component'
import {
  TRAINING_REPORT_SLUG_CORE,
  TRAINING_REPORT_SLUG_DWB,
  trainingReportSlugToVariant,
  trainingReportVariantToSlug,
} from './routes'
import {
  getDwbColumnEnabledFromSite,
  siteHasDwbTrainingProgram,
} from './helpers'
import {
  VARIANT_K12_DWB,
  VARIANT_K8_CORE,
} from 'components/SiteUserTrainingGrid/constants'
import {
  operations as sitesOperations,
  selectors as sitesSelectors,
} from 'store/sitesManager'
import { operations as lmsOperations } from 'store/lmsManager'

export const TrainingReportContainer = props => {
  const {
    data,
    error,
    fetchSiteUsers,
    fetchTrainingCompletion,
    history,
    isLoading,
    location,
    match,
    pushTo,
    site,
    siteId,
    siteUsers,
    sites,
  } = props

  const reportSlug = match.params.reportSlug

  const [reportVariant, setReportVariant] = useState(() =>
    trainingReportSlugToVariant(reportSlug),
  )

  useEffect(() => {
    setReportVariant(trainingReportSlugToVariant(reportSlug))
  }, [reportSlug])

  useEffect(() => {
    if (!siteId || Number.isNaN(siteId)) {
      return
    }
    if (reportSlug === undefined) {
      history.replace(`/training-report/${siteId}/${TRAINING_REPORT_SLUG_CORE}`)
      return
    }
    if (
      reportSlug !== TRAINING_REPORT_SLUG_CORE &&
      reportSlug !== TRAINING_REPORT_SLUG_DWB
    ) {
      history.replace(`/training-report/${siteId}/${TRAINING_REPORT_SLUG_CORE}`)
    }
  }, [siteId, reportSlug, history])

  useEffect(() => {
    if (!siteId || Number.isNaN(siteId) || !site) {
      return
    }
    if (siteHasDwbTrainingProgram(site)) {
      return
    }
    if (
      reportSlug === TRAINING_REPORT_SLUG_DWB ||
      trainingReportSlugToVariant(reportSlug) === VARIANT_K12_DWB
    ) {
      history.replace(`/training-report/${siteId}/${TRAINING_REPORT_SLUG_CORE}`)
    }
  }, [site, siteId, reportSlug, history])

  useEffect(() => {
    if (!siteUsers) {
      fetchSiteUsers(siteId)
      fetchTrainingCompletion(siteId)
    }
  }, [siteId, pushTo])

  const pendingUsersCount = siteUsers?.filter(
    user => user?.status === 'Invited',
  ).length
  const activeUsers = siteUsers?.filter(user => user?.status === 'Active')

  const redirectToUserManagement = () => {
    pushTo(`/user-management/${siteId}`)
  }

  const onReportVariantChange = useCallback(
    variant => {
      if (variant === VARIANT_K12_DWB && !siteHasDwbTrainingProgram(site)) {
        return
      }
      setReportVariant(variant)
      if (!siteId || Number.isNaN(siteId)) {
        return
      }
      pushTo(
        `/training-report/${siteId}/${trainingReportVariantToSlug(variant)}`,
      )
    },
    [siteId, pushTo, site],
  )

  const onSiteChange = e => {
    const { value } = e
    const newSiteId = value.id || {}
    const isDifferentSite = siteId !== newSiteId

    if (isDifferentSite) {
      const allowDwb = siteHasDwbTrainingProgram(value)
      const slug = allowDwb
        ? trainingReportVariantToSlug(reportVariant)
        : TRAINING_REPORT_SLUG_CORE
      pushTo(`/training-report/${newSiteId}/${slug}`)
      if (!allowDwb && reportVariant === VARIANT_K12_DWB) {
        setReportVariant(VARIANT_K8_CORE)
      }
    }
  }

  let k8Sites = sites?.filter(site =>
    site?.netSuiteAssets?.some(netSuiteAsset => {
      const programCodes = netSuiteAsset?.product?.programCodes || []
      const hasK8Program = programCodes.some(code =>
        ['K8P', 'K12P', 'MSP'].includes(code),
      )
      return (
        hasK8Program &&
        netSuiteAsset.isActive &&
        netSuiteAsset?.status !== 'Expired'
      )
    }),
  )

  const showProgramSelect = siteHasDwbTrainingProgram(site)
  const dwbColumnEnabled = getDwbColumnEnabledFromSite(site)

  return (
    <TrainingReport
      data={data}
      dwbColumnEnabled={dwbColumnEnabled}
      error={error}
      isLoading={isLoading}
      location={location}
      onReportVariantChange={onReportVariantChange}
      onSiteChange={onSiteChange}
      pendingUsersCount={pendingUsersCount}
      redirectToUserManagement={redirectToUserManagement}
      reportVariant={reportVariant}
      showProgramSelect={showProgramSelect}
      site={site}
      siteId={siteId}
      siteUsers={activeUsers}
      sites={k8Sites}
    />
  )
}

TrainingReportContainer.propTypes = {
  addNotification: PropTypes.func.isRequired,
  data: PropTypes.array,
  error: PropTypes.object,
  fetchSiteUsers: PropTypes.func.isRequired,
  fetchTrainingCompletion: PropTypes.func.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    query: PropTypes.object,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      reportSlug: PropTypes.string,
      siteId: PropTypes.string,
    }),
  }).isRequired,
  message: PropTypes.string,
  pushTo: PropTypes.func.isRequired,
  site: PropTypes.object,
  siteId: PropTypes.number,
  siteUsers: PropTypes.array,
  sites: PropTypes.array,
}

TrainingReportContainer.defaultProps = {
  isLoading: false,
  data: [],
  error: null,
  message: '',
}

const mapStateToProps = (state, { match }) => {
  const {
    router,
    sitesManager: {
      message,
      sites: { data: sites, isLoading: isSitesLoading },
      siteUsers: { isLoading: isSiteUsersLoading },
    },
    lmsManager: { isFetching: isTrainingCompletionLoading },
  } = state

  const isLoading =
    isSitesLoading || isSiteUsersLoading || isTrainingCompletionLoading
  let { siteId } = match.params
  siteId = parseInt(siteId, 10)
  const site = !siteId ? sites[0] : sites.find(site => site.id === siteId)

  const siteUsers = sitesSelectors.selectSiteUsersWithTrainingCompletionBySiteId(
    state,
    siteId,
  )

  const { location } = router

  return {
    isLoading,
    location,
    message,
    site,
    siteId,
    sites,
    siteUsers,
  }
}

const mapDispatchToProps = {
  addNotification: notificationActions.addNotification,
  fetchSiteUsers: sitesOperations.fetchSiteUsers,
  fetchTrainingCompletion: lmsOperations.fetchTrainingCompletion,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TrainingReportContainer)
